import { NgTemplateOutlet } from '@angular/common';
import { Component, Directive, TemplateRef, computed, contentChild, effect, inject, input, signal } from '@angular/core';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

/** From `minWidth` (px, viewport) up, `itemsVisible` items are shown side by side */
export interface PagerBreakpoint {
  minWidth: number;
  itemsVisible: number;
}

/** The template each item is rendered with: `<ng-template appPagerItem let-item>` */
@Directive({ selector: 'ng-template[appPagerItem]', standalone: true })
export class PagerItemDirective<T> {
  template = inject<TemplateRef<{ $implicit: T }>>(TemplateRef);
}

/**
 * A row of items paged one at a time, with the title, the visible range ("1-2 of 3") and the arrows in a header above
 * the items, so the arrows never cover them (KOR-15207, Figma 8329:1913). The arrows are left out when every item fits.
 * Written for reuse: anything with a list and an item template can page through it.
 */
@Component({
  selector: 'app-pager',
  templateUrl: './pager.component.html',
  styleUrls: ['./pager.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, RktIconComponent],
})
export class PagerComponent<T> {
  items = input<T[]>([]);
  breakpoints = input<PagerBreakpoint[]>([{ minWidth: 0, itemsVisible: 1 }]);
  /** What the items are, for the arrows' accessible names: "Previous attachments" */
  itemLabel = input('items');

  itemTemplate = contentChild.required(PagerItemDirective<T>);

  private viewportWidthChanged = signal(0);
  // The position belongs to the list it was set on: a new list starts from its first item without an extra render pass
  private position = signal<{ list: T[] | null; start: number }>({ list: null, start: 0 });
  private start = computed(() => (this.position().list === this.items() ? this.position().start : 0));

  itemsVisible = computed(() => {
    this.viewportWidthChanged();
    const fitting = [...this.breakpoints()]
      .sort((a, b) => a.minWidth - b.minWidth)
      .filter(({ minWidth }) => window.matchMedia(`(min-width: ${minWidth}px)`).matches);
    return Math.max(1, fitting.at(-1)?.itemsVisible ?? 1);
  });

  // Clamped, so widening the viewport near the end shows a full last page instead of a gap
  private first = computed(() => Math.min(this.start(), Math.max(0, this.items().length - this.itemsVisible())));

  visibleItems = computed(() => this.items().slice(this.first(), this.first() + this.itemsVisible()));
  canPage = computed(() => this.items().length > this.itemsVisible());
  atStart = computed(() => this.first() === 0);
  atEnd = computed(() => this.first() + this.itemsVisible() >= this.items().length);

  range = computed(() => {
    const total = this.items().length;
    if (!total) return '';
    const from = this.first() + 1;
    const to = Math.min(this.first() + this.itemsVisible(), total);
    return from === to ? `${from} of ${total}` : `${from}-${to} of ${total}`;
  });

  constructor() {
    // Re-measure when the viewport crosses one of the breakpoints; the listeners go when the inputs change or the pager does
    effect((onCleanup) => {
      const onChange = () => this.viewportWidthChanged.update((n) => n + 1);
      const queries = this.breakpoints().map(({ minWidth }) => window.matchMedia(`(min-width: ${minWidth}px)`));
      queries.forEach((query) => query.addEventListener('change', onChange));
      onCleanup(() => queries.forEach((query) => query.removeEventListener('change', onChange)));
    });
  }

  previous(): void {
    this.position.set({ list: this.items(), start: Math.max(0, this.first() - 1) });
  }

  next(): void {
    if (!this.atEnd()) this.position.set({ list: this.items(), start: this.first() + 1 });
  }
}
